import { ConvexError, type Infer, v } from 'convex/values'

import { isLegacyGoogleDefaultUrl } from './googleAvatar'
import { habitIcon } from './habitIcons'
import type { PlanTier } from './plan'

// Custom "done" dots for a habit (Pro). Unset = the classic look, unchanged.
// With a custom style, missed days are an empty ring (or the Image style's
// optional missed-day image where images show); skipped and rest days keep
// their usual look.

export const dotSlotValidator = v.union(v.literal('done'), v.literal('missed'))
export type DotSlot = Infer<typeof dotSlotValidator>

const dotImageValidator = { storageId: v.id('_storage'), prompt: v.string() }

export const dotStyleValidator = v.union(
  v.object({ kind: v.literal('emoji'), emoji: v.string() }),
  v.object({ kind: v.literal('smiley') }),
  v.object({ kind: v.literal('icon') }),
  v.object({ kind: v.literal('photo') }),
  v.object({
    kind: v.literal('image'),
    ...dotImageValidator,
    missedImage: v.optional(v.object(dotImageValidator)),
  }),
)
export type DotStyle = Infer<typeof dotStyleValidator>

export const dotStyleInputValidator = v.union(
  v.object({ kind: v.literal('default') }),
  dotStyleValidator,
)
export type DotStyleInput = Infer<typeof dotStyleInputValidator>

// What clients render: the saved style with the image resolved to a URL, or
// null for the default look (unset, or saved while Pro but now on Free).
export const dotViewValidator = v.union(
  v.null(),
  v.object({ kind: v.literal('emoji'), emoji: v.string() }),
  v.object({ kind: v.literal('smiley') }),
  v.object({ kind: v.literal('icon') }),
  v.object({ kind: v.literal('photo') }),
  v.object({
    kind: v.literal('image'),
    url: v.union(v.string(), v.null()),
    missedUrl: v.union(v.string(), v.null()),
  }),
)
export type DotView = Infer<typeof dotViewValidator>

// Photo dots are free: days with a photo show it, others keep the plain dot.
export const photoAskValidator = v.union(
  v.literal('always'),
  v.literal('sometimes'),
  v.literal('never'),
)
export type PhotoAsk = Infer<typeof photoAskValidator>

export const SMILEY_DONE = '😊'
export const EMOJI_PRESETS = [
  '🏃',
  '💧',
  '📖',
  '🧘',
  '💪',
  '🥗',
  '😴',
  '✍️',
  '🎸',
  '🌱',
  '🦷',
  '🚶',
]
export const DOT_IMAGE_DAILY_LIMIT = 10
export const DOT_PROMPT_MAX = 200
// Below this a glyph is unreadable, so cells keep the plain filled dot.
export const DOT_MARK_MIN_PX = 9
// Images show as a round crop down to row size; smaller cells show the icon.
export const DOT_IMAGE_MIN_PX = 12

export type DotErrorCode =
  | 'proRequired'
  | 'dailyCap'
  | 'unsafePrompt'
  | 'otherPerson'
  | 'badPrompt'
  | 'badEmoji'
  | 'badImage'
  | 'faceUnavailable'
  | 'needSelfie'
  | 'notFound'
  | 'generationFailed'
export type DotError = { kind: 'habitDots'; code: DotErrorCode }
export const dotError = (code: DotErrorCode) =>
  new ConvexError<DotError>({ kind: 'habitDots', code })
export function dotErrorCode(err: unknown): DotErrorCode | null {
  const data = (err as { data?: unknown })?.data as Partial<DotError> | undefined
  return data?.kind === 'habitDots' && data.code ? data.code : null
}

const EMOJI_UNIT = String.raw`(?!\p{Regional_Indicator})(?:\p{Extended_Pictographic}|\p{Emoji_Presentation})(?:️|\p{Emoji_Modifier})?`
const SINGLE_EMOJI = new RegExp(
  String.raw`^(?:\p{Regional_Indicator}{2}|[#*0-9]️?⃣|${EMOJI_UNIT}(?:‍${EMOJI_UNIT})*️?)$`,
  'u',
)

/** One emoji grapheme (ZWJ families, skin tones, flags and keycaps included). */
export function isSingleEmoji(s: string): boolean {
  if (!s || s.length > 32 || !SINGLE_EMOJI.test(s)) return false
  type Segmenter = new (
    locale: undefined,
    opts: { granularity: 'grapheme' },
  ) => { segment: (s: string) => Iterable<unknown> }
  const Seg = (Intl as unknown as { Segmenter?: Segmenter }).Segmenter
  if (!Seg) return true
  return [...new Seg(undefined, { granularity: 'grapheme' }).segment(s)].length === 1
}

export function normalizeDotStyle(style: DotStyleInput): DotStyle | undefined {
  if (style.kind === 'default') return undefined
  if (style.kind === 'emoji') {
    const emoji = style.emoji.trim()
    if (!isSingleEmoji(emoji)) throw dotError('badEmoji')
    return { kind: 'emoji', emoji }
  }
  if (style.kind === 'image') {
    const clip = (p: string) => p.trim().slice(0, DOT_PROMPT_MAX)
    const { missedImage } = style
    return {
      kind: 'image',
      storageId: style.storageId,
      prompt: clip(style.prompt),
      ...(missedImage && {
        missedImage: { storageId: missedImage.storageId, prompt: clip(missedImage.prompt) },
      }),
    }
  }
  return { kind: style.kind }
}

/** Downgraded users keep their saved style but see default dots until Pro again. */
export function dotView(
  style: DotStyle | undefined,
  tier: PlanTier,
  urls: { url: string | null; missedUrl: string | null } = { url: null, missedUrl: null },
): DotView {
  if (!style || (tier === 'free' && style.kind !== 'photo')) return null
  if (style.kind === 'image') return { kind: 'image', ...urls }
  return style
}

/** Storage files a style holds (done image, then the missed-day image). */
export function dotStyleFiles(style: DotStyle | undefined) {
  if (style?.kind !== 'image') return []
  return [style.storageId, ...(style.missedImage ? [style.missedImage.storageId] : [])]
}

export type DotGlyph =
  | { type: 'emoji'; text: string }
  | { type: 'icon'; name: string }
  | { type: 'image'; url: string }

type HabitLike = { icon?: string | null; title: string; category?: string | null }

/** What a done cell of `size` px shows; null = the plain filled dot. */
export function doneGlyph(view: DotView, size: number, habit: HabitLike): DotGlyph | null {
  if (!view || view.kind === 'photo' || size < DOT_MARK_MIN_PX) return null
  if (view.kind === 'emoji') return { type: 'emoji', text: view.emoji }
  if (view.kind === 'smiley') return { type: 'emoji', text: SMILEY_DONE }
  if (view.kind === 'image' && view.url && size >= DOT_IMAGE_MIN_PX)
    return { type: 'image', url: view.url }
  return { type: 'icon', name: habitIcon(habit) }
}

/**
 * A scheduled day that was missed: an empty ring (every style, default too),
 * the Image style's missed-day image where images show, or 'plain' below
 * DOT_MARK_MIN_PX where a ring wouldn't read.
 */
// A missed day is the plain faint cell, unless the user set their own missed-day image.
export function missedLook(view: DotView, size: number): 'plain' | 'ring' | DotGlyph {
  if (size < DOT_MARK_MIN_PX) return 'plain'
  if (view?.kind === 'image' && view.missedUrl) return { type: 'image', url: view.missedUrl }
  return 'plain'
}

// ─── Profile photo as the selfie ─────────────────────────────────────────────

/**
 * The user's avatar as a fetchable photo URL, or null when it isn't one we
 * trust to be a real photo: only Google profile photos (asked for at selfie
 * size) and photos uploaded to our own storage. Generated avatars, Gravatar
 * and any other host are skipped (the server must not fetch arbitrary URLs).
 */
export function avatarPhotoUrl(image: string | null | undefined, storageOrigin?: string) {
  if (!image) return null
  let u: URL
  try {
    u = new URL(image)
  } catch {
    return null
  }
  if (u.protocol !== 'https:') return null
  if (u.hostname.endsWith('.googleusercontent.com')) {
    if (isLegacyGoogleDefaultUrl(image)) return null
    if (/\/s\d+(-c)?\//.test(u.pathname)) {
      u.pathname = u.pathname.replace(/\/s\d+(-c)?\//, '/s512-c/')
      return u.href
    }
    return u.href.replace(/=[^/]*$/, '') + '=s512-c'
  }
  if (
    storageOrigin &&
    u.origin === new URL(storageOrigin).origin &&
    u.pathname.startsWith('/api/storage/')
  )
    return u.href
  return null
}

/**
 * Whether small RGBA pixels look like a photo rather than a letter or cartoon
 * avatar: flat artwork has few distinct colours, photos have many.
 */
export function looksLikePhoto(rgba: Uint8Array, minColors = 64) {
  const seen = new Set<number>()
  for (let i = 0; i + 3 < rgba.length; i += 4) {
    seen.add(
      rgba[i + 3] < 128
        ? -1
        : ((rgba[i] >> 4) << 8) | ((rgba[i + 1] >> 4) << 4) | (rgba[i + 2] >> 4),
    )
    if (seen.size >= minColors) return true
  }
  return false
}

// ─── Image prompts ────────────────────────────────────────────────────────────

const SEXUAL =
  /\b(nude|nudes|naked|nsfw|sex|sexy|sexual|porn\w*|boobs?|breasts?|nipples?|genital\w*|penis|vagina|erotic\w*|lingerie|topless|bottomless|horny|fetish\w*|onlyfans|strip(per|ping)|xxx|hentai|undress\w*)\b/i
const VIOLENT =
  /\b(gore|gory|bloody|blood|bleeding|decapitat\w*|behead\w*|corpses?|dead bod(y|ies)|mutilat\w*|dismember\w*|murder\w*|suicide|self[- ]?harm|torture\w*|massacre|guns?|rifles?|shoot(ing)? (him|her|them|people|someone)|stab\w*|kill(ing)? (him|her|them|people|someone|myself)|bomb\w*|terroris\w*|gun ?shot|severed|entrails|guts spill\w*)\b/i
const HATEFUL =
  /\b(nazis?|swastikas?|kkk|hitler|white power|white supremac\w*|heil|n[i1]gg\w*|f[a4]gg?\w*|k[i1]kes?|ch[i1]nks?|sp[i1]cs?|retards?)\b/i
const OTHER_PERSON =
  /\b(my|our|his|her|their) (wife|husband|partner|girlfriend|boyfriend|friend|friends|mom|mum|mother|dad|father|boss|son|daughter|brother|sister|kids?|children|baby|coworker|colleague|neighbou?r|ex|crush)\b/i
const FAMOUS =
  /\b(trump|biden|obama|putin|zelensk\w*|elon|musk|taylor swift|beyonc[eé]|kardashian\w*|rihanna|drake|kanye|oprah|zuckerberg|bezos|ronaldo|messi|lebron|the rock|dwayne johnson|arnold|schwarzenegger|kim jong|xi jinping|modi|king charles|the pope|pope francis|emma watson|scarlett johansson|brad pitt|tom cruise|ariana grande|billie eilish|bts|blackpink|celebrity|celebrities|famous person)\b/i
// "Look like Jane Doe" — a capitalised full name in a likeness phrase.
const NAMED_LIKENESS = /\b(?:looks? like|featuring|starring)\s+(?:[A-Z][a-z]+\s+){1,2}[A-Z][a-z]+\b/

export type PromptIssue = 'badPrompt' | 'unsafePrompt' | 'otherPerson'

/** Fast local screen before any model sees the prompt (a model check follows). */
export function promptIssue(prompt: string): PromptIssue | null {
  const p = prompt.trim()
  if (p.length < 2 || p.length > DOT_PROMPT_MAX) return 'badPrompt'
  if (SEXUAL.test(p) || VIOLENT.test(p) || HATEFUL.test(p)) return 'unsafePrompt'
  if (OTHER_PERSON.test(p) || FAMOUS.test(p) || NAMED_LIKENESS.test(p)) return 'otherPerson'
  return null
}

const FACE =
  /\b(me|myself|my face|my likeness|my selfie|selfie|my body|my head|i am|i'm|of me|like me)\b/i

/** The prompt is about the user themself, so it needs their reference selfie. */
export function wantsFace(prompt: string): boolean {
  return FACE.test(prompt)
}

const STICKER_STYLE =
  'simple bold sticker illustration, one centered subject filling most of the frame, plain solid light background, thick clean outlines, flat vivid colours, high contrast, readable at very small sizes, square composition, no text, no letters, no border'

export function stickerPrompt(prompt: string, face: boolean): string {
  const subject = prompt.trim().replace(/\s+/g, ' ')
  return face
    ? `Turn the person in the reference photo into a ${STICKER_STYLE}. Keep their face and likeness recognisable. Scene: ${subject}. Head and shoulders or full body, friendly and wholesome.`
    : `${subject}. Style: ${STICKER_STYLE}. Wholesome and friendly.`
}

// ─── Face (reference-photo) generation ───────────────────────────────────────

// Cloudflare Workers AI FLUX.2 takes reference images, on the same Cloudflare
// credentials as the text-only generator. HABIT_FACE_IMAGES_PROVIDER=off turns
// face images off; HABIT_FACE_IMAGE_MODEL picks another FLUX.2 model.
export const FACE_PROVIDERS = ['cloudflare'] as const
export type FaceProviderName = (typeof FACE_PROVIDERS)[number]

export function faceProviderName(env: Record<string, string | undefined>): FaceProviderName | null {
  const chosen = (env.HABIT_FACE_IMAGES_PROVIDER ?? 'cloudflare').trim().toLowerCase()
  if (chosen !== 'cloudflare') return null
  const account = env.CLOUDFLARE_ACCOUNT_ID || env.CF_AI_GATEWAY_ACCOUNT_ID
  return account && env.CLOUDFLARE_API_TOKEN ? 'cloudflare' : null
}
